import React, { useCallback, useState, useRef, forwardRef, useEffect } from "react";
import "./CardFront.less";

interface CardFrontProps {
  imageUrl?: string;
  height?: string;
  onImageLoad?: (dimensions: { width: number; height: number }) => void;
  imageRef?: React.RefObject<HTMLImageElement | null>;
}

const maxLargeDimension = 3;
const maxSmallDimension = 2;

// Portrait and landscape limits based on dimension constants
const MIN_ASPECT_RATIO = maxSmallDimension / maxLargeDimension;
const MAX_ASPECT_RATIO = maxLargeDimension / maxSmallDimension;

/**
 * Front side of the card preview that displays the user's uploaded photo
 * - Handles image loading, aspect ratio calculations, and responsiveness
 * - Constrains images to acceptable aspect ratios and provides visual feedback during loading
 */
export const CardFront = forwardRef<HTMLDivElement, CardFrontProps>(
  ({ imageUrl, height = "100%", onImageLoad, imageRef: providedImageRef }, ref) => {
    const [isVisible, setIsVisible] = useState(false);
    const [aspectRatioStyle, setAspectRatioStyle] = useState<string | undefined>();
    const wrapperRef = useRef<HTMLDivElement>(null);
    const internalImageRef = useRef<HTMLImageElement>(null);
    // Use provided ref or internal ref
    const imageRef = providedImageRef || internalImageRef;

    // Handles image loading and aspect ratio calculation
    // Constrains very wide or tall images to acceptable ratios while
    // preserving natural ratios for images within acceptable ranges
    const handleImageLoad = useCallback(
      (event: React.SyntheticEvent<HTMLImageElement>) => {
        const img = event.currentTarget;
        const dimensions = {
          width: img.naturalWidth,
          height: img.naturalHeight
        };

        const aspectRatio = dimensions.width / dimensions.height;

        // If image exceeds max landscape ratio, constrain to max ratio
        if (aspectRatio > MAX_ASPECT_RATIO) {
          setAspectRatioStyle(`${maxLargeDimension} / ${maxSmallDimension}`);
        }
        // If image exceeds max portrait ratio, constrain to max ratio
        else if (aspectRatio < MIN_ASPECT_RATIO) {
          setAspectRatioStyle(`${maxSmallDimension} / ${maxLargeDimension}`);
        }
        // Use the image's natural aspect ratio
        else {
          setAspectRatioStyle(`${dimensions.width} / ${dimensions.height}`);
        }

        onImageLoad?.(dimensions);
        setIsVisible(true);
      },
      [onImageLoad]
    );

    useEffect(() => {
      if (!imageUrl) {
        setIsVisible(true);
      }
    }, [imageUrl]);

    return (
      <div
        ref={(node) => {
          wrapperRef.current = node;
          if (typeof ref === "function") {
            ref(node);
          } else if (ref) {
            ref.current = node;
          }
        }}
        className={
          isVisible ? "card-front__wrapper card-front__wrapper--visible" : "card-front__wrapper"
        }
        style={{ height }}
      >
        <div className="card-front__inner">
          <div className="card-front__image-container">
            <div
              className="card-front__image-container-inner"
              style={aspectRatioStyle ? { aspectRatio: aspectRatioStyle } : undefined}
            >
              {imageUrl ? (
                <img
                  ref={imageRef}
                  className="card-front__photo"
                  src={imageUrl}
                  alt="Uploaded photo"
                  onLoad={handleImageLoad}
                />
              ) : (
                <img
                  ref={imageRef}
                  className="card-front__photo"
                  src="https://wvusstatic.com/sak/questionnaire/creamy/card_front_placeholder.svg"
                  alt="Placeholder"
                  onLoad={handleImageLoad}
                />
              )}
            </div>
          </div>
          <img
            className="card-front__logo"
            src="https://wvusstatic.com/sak/questionnaire/wv-logo_on_card.svg"
            alt="WV Logo"
          />
        </div>
      </div>
    );
  }
);
